import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../lib/auth-context.js";
import { listBills } from "../../lib/api-modules/bills.js";
import { listIncomeEvents } from "../../lib/api-modules/income-events.js";
import { listSpendingTransactions } from "../../lib/api-modules/spending-transactions.js";
import {
  updateFireAssumptions,
  type UpdateFireAssumptionsInput,
} from "../../lib/api-modules/households.js";
import { fireNumbers, savingsRate, timeToFI } from "../../lib/util/fire.js";
import {
  ALL_CITY_CODES,
  COL_INDEX_2026,
  colMultiplier,
  type CityCode,
} from "../../lib/data/cost-of-living.js";
import { formatCentsCompact } from "../../lib/util/money.js";
import { useToast } from "../toast.js";
import {
  Card,
  Pill,
  SelectField,
} from "../design-system/index.js";

/**
 * Phase 6 Session 4 — geographic arbitrage tile. The "FIRE-community
 * headline gimmick": leave NYC for Pittsburgh, retire 7 years sooner.
 *
 * Reads `fire_assumptions.current_city_code` (defaults to NYC). User
 * picks a destination city; tile recomputes annual spend via
 * `colMultiplier(current, destination)` and re-runs `timeToFI` to show
 * the FI-date delta. The current city persists via PATCH to
 * `/v1/households/{id}/fire-assumptions`; the destination is
 * session-state only (the user is exploring, not committing).
 *
 * Privacy mode masks $ amounts; the years-delta stays visible — it's
 * the lesson the spouse cares about.
 */

const DEFAULT_CURRENT_CITY: CityCode = "NYC";
const DEFAULT_RETURN_PCT = 5;
const DEFAULT_WR_PCT = 4;

interface Props {
  readonly hideValues: boolean;
  readonly fiAssetsCents: number;
}

export function GeographicArbitrageTile({
  hideValues,
  fiAssetsCents,
}: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const trailingFrom = useMemo(() => {
    const d = new Date(today);
    d.setUTCMonth(d.getUTCMonth() - 12);
    return d.toISOString().slice(0, 10);
  }, [today]);

  const billsQuery = useQuery({
    queryKey: ["bills", { includeArchived: false }],
    queryFn: () => listBills(false),
  });
  const incomeQuery = useQuery({
    queryKey: ["income-events", { from: trailingFrom }],
    queryFn: () => listIncomeEvents({ from: trailingFrom }),
  });
  const spendQuery = useQuery({
    queryKey: ["spending-transactions", { from: trailingFrom, to: today }],
    queryFn: () =>
      listSpendingTransactions({ from: trailingFrom, to: today }),
  });

  const a = user?.fire_assumptions;
  const currentCity = (a?.current_city_code ?? DEFAULT_CURRENT_CITY);
  const isKnownCurrent = currentCity in COL_INDEX_2026;

  const [destination, setDestination] = useState<CityCode>(
    currentCity === "PIT" ? "NYC" : "PIT",
  );

  const isLoading = billsQuery.isLoading || incomeQuery.isLoading || spendQuery.isLoading;

  const computed = useMemo(() => {
    if (isLoading || !isKnownCurrent) return null;
    const sr = savingsRate({
      income_events: incomeQuery.data?.income_events ?? [],
      bills: billsQuery.data?.bills ?? [],
      spending_transactions: spendQuery.data?.spending_transactions ?? [],
      today,
      lookback_months: 12,
    });
    if (sr.gross_income_cents <= 0) return null;
    const baseAnnualSpend =
      a?.annual_spend_override_cents ?? sr.annualized_spend_cents;
    const wrPct = a?.withdrawal_rate_pct ?? DEFAULT_WR_PCT;
    const returnPct = a?.expected_return_rate_pct ?? DEFAULT_RETURN_PCT;

    const baselineNumbers = fireNumbers({
      annual_spend_cents: baseAnnualSpend,
      withdrawal_rate_pct: wrPct,
    });
    const baselineTime = timeToFI({
      current_net_worth_cents: fiAssetsCents,
      monthly_net_savings_cents: sr.monthly_net_savings_cents,
      annual_return_rate_pct: returnPct,
      fire_target_cents: baselineNumbers.regular_cents,
      today,
    });

    const mult = colMultiplier(currentCity as CityCode, destination);
    const moveAnnualSpend = Math.round(baseAnnualSpend * mult);
    const moveNumbers = fireNumbers({
      annual_spend_cents: moveAnnualSpend,
      withdrawal_rate_pct: wrPct,
    });
    const moveTime = timeToFI({
      current_net_worth_cents: fiAssetsCents,
      monthly_net_savings_cents: sr.monthly_net_savings_cents,
      annual_return_rate_pct: returnPct,
      fire_target_cents: moveNumbers.regular_cents,
      today,
    });

    return {
      multiplier: mult,
      baselineAnnualSpendCents: baseAnnualSpend,
      moveAnnualSpendCents: moveAnnualSpend,
      baselineFireTargetCents: baselineNumbers.regular_cents,
      moveFireTargetCents: moveNumbers.regular_cents,
      baselineYearsToFI: baselineTime.years_to_fi,
      moveYearsToFI: moveTime.years_to_fi,
      baselineFiDate: baselineTime.fi_date,
      moveFiDate: moveTime.fi_date,
    };
  }, [
    isLoading,
    isKnownCurrent,
    billsQuery.data,
    incomeQuery.data,
    spendQuery.data,
    today,
    a,
    currentCity,
    destination,
    fiAssetsCents,
  ]);

  const cityMutation = useMutation({
    mutationFn: (input: UpdateFireAssumptionsInput) => {
      if (!user) throw new Error("Not signed in");
      return updateFireAssumptions(user.primary_household_id, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.show("Home city saved.");
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Failed to save";
      toast.show(msg);
    },
  });

  function onSaveCurrentCity(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const next = (e.currentTarget.elements.namedItem("city") as HTMLSelectElement)
      .value;
    cityMutation.mutate({ current_city_code: next });
  }

  const yearsDelta =
    computed &&
    computed.baselineYearsToFI !== null &&
    computed.moveYearsToFI !== null
      ? computed.baselineYearsToFI - computed.moveYearsToFI
      : null;

  return (
    <Card className="mb-6" data-testid="geographic-arbitrage-tile">
      <div className="mb-4">
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
          Geographic arbitrage
        </p>
        <p className="mt-0.5 text-xs text-[var(--color-muted)]">
          NUMBEO 2026 cost-of-living multiplier × your annual spend → time-to-FI delta.
        </p>
      </div>

      <form
        onSubmit={onSaveCurrentCity}
        className="mb-4 flex flex-wrap items-end gap-3"
      >
        <SelectField
          label="Home city"
          name="city"
          value={currentCity}
          onChange={(e) => {
            cityMutation.mutate({ current_city_code: e.target.value });
          }}
          className="min-w-[14rem]"
        >
          {ALL_CITY_CODES.map((c) => (
            <option key={c} value={c}>
              {COL_INDEX_2026[c].city}, {COL_INDEX_2026[c].country}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="If we moved to"
          value={destination}
          onChange={(e) => setDestination(e.target.value as CityCode)}
          className="min-w-[14rem]"
        >
          {ALL_CITY_CODES.filter((c) => c !== currentCity).map((c) => (
            <option key={c} value={c}>
              {COL_INDEX_2026[c].city}, {COL_INDEX_2026[c].country}
            </option>
          ))}
        </SelectField>
      </form>

      {!computed ? (
        <p className="rounded-md border border-dashed border-[var(--color-border-default)] p-6 text-center text-sm text-[var(--color-muted)]">
          {isLoading
            ? "Loading…"
            : !isKnownCurrent
              ? `"${currentCity}" isn't in the COL dataset; pick a city above.`
              : "Add income events to see the relocation delta."}
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
              Cost-of-living multiplier
            </p>
            <p className="mt-1 font-mono text-2xl font-medium tabular-nums text-[var(--color-text)]">
              {computed.multiplier.toFixed(2)}×
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--color-muted)]">
              {currentCity} = {COL_INDEX_2026[currentCity as CityCode]?.col_index.toFixed(1)}{" "}
              · {destination} ={" "}
              {COL_INDEX_2026[destination].col_index.toFixed(1)}
            </p>
          </div>
          <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
              Annual spend after move
            </p>
            <p className="mt-1 font-mono text-2xl font-medium tabular-nums text-[var(--color-text)]">
              {formatCentsCompact(computed.moveAnnualSpendCents, {
                hidden: hideValues,
              })}
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--color-muted)]">
              from{" "}
              {formatCentsCompact(computed.baselineAnnualSpendCents, {
                hidden: hideValues,
              })}{" "}
              today
            </p>
          </div>
          <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
              Time-to-FI shift
            </p>
            <p className="mt-1 font-mono text-2xl font-medium tabular-nums text-[var(--color-text)]">
              {yearsDelta === null
                ? "—"
                : yearsDelta > 0
                  ? `−${yearsDelta.toFixed(1)} yr`
                  : `+${(-yearsDelta).toFixed(1)} yr`}
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--color-muted)]">
              {computed.moveFiDate
                ? `target ${computed.moveFiDate.slice(0, 7)}`
                : "beyond horizon"}
            </p>
          </div>
        </div>
      )}

      {computed && yearsDelta !== null && yearsDelta > 0.5 && (
        <div className="mt-4 rounded-md bg-[var(--color-accent-soft)] px-4 py-3 text-sm text-[var(--color-accent)]">
          Moving to{" "}
          <strong>{COL_INDEX_2026[destination].city}</strong> shaves{" "}
          <strong>{yearsDelta.toFixed(1)} years</strong> off your time-to-FI.
        </div>
      )}
      {computed && yearsDelta !== null && yearsDelta < -0.5 && (
        <div className="mt-4 rounded-md bg-[var(--color-warning-soft)] px-4 py-3 text-sm text-[var(--color-warning)]">
          Moving to <strong>{COL_INDEX_2026[destination].city}</strong> would push
          FI <strong>{(-yearsDelta).toFixed(1)} years</strong> later (higher cost of living).
        </div>
      )}

      <div className="mt-3 text-[11px] text-[var(--color-muted)]">
        Source: NUMBEO Cost of Living Plus Rent Index 2026 · NYC = 100 baseline ·{" "}
        <Pill tone="neutral">approximate</Pill>
      </div>
    </Card>
  );
}
